import { FormEvent, useState, useCallback, useMemo, useEffect } from 'react';

import { debounce } from 'lodash-es';

import { SEARCH_DEBOUNCE_TIME } from 'src/components/pipelines/constants';
import SearchField from 'src/components/search-field';
import { publishUiEvent } from 'src/utils/analytics/publish';

const publishSearchAnalytics = (
  name: string,
  attributes?: Record<string, any>
) => {
  publishUiEvent({
    action: 'changed',
    actionSubject: 'search',
    actionSubjectId: `${name}Search`,
    source: 'pipelinesTestManagement',
    ...(attributes && { attributes }),
  });
};

export const SearchFilter = ({
  name,
  placeholder,
  onFilterChange,
  filterValue,
  width,
}: {
  name: string;
  placeholder: string;
  onFilterChange: (value?: string) => void;
  filterValue: string;
  width?: string;
}) => {
  const [currentSearch, setCurrentSearch] = useState(filterValue || '');

  const debouncedFilterChange = useMemo(
    () =>
      debounce((value: string) => {
        onFilterChange(value);
        publishSearchAnalytics(name);
      }, SEARCH_DEBOUNCE_TIME),
    [onFilterChange, name]
  );

  useEffect(() => {
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [debouncedFilterChange]);

  const onChange = useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      setCurrentSearch(value);
      if (value === '') {
        debouncedFilterChange.cancel();
        onFilterChange(undefined);
        publishSearchAnalytics(name, { cleared: true });
      } else {
        debouncedFilterChange(value);
      }
    },
    [debouncedFilterChange, onFilterChange, name]
  );

  const clearFilter = useCallback(() => {
    debouncedFilterChange.cancel();
    setCurrentSearch('');
    onFilterChange(undefined);
    publishSearchAnalytics(name, { cleared: true });
  }, [debouncedFilterChange, onFilterChange, name]);

  return (
    <SearchField
      width={width}
      currentSearch={currentSearch}
      handleFilterChange={onChange}
      placeholder={placeholder}
      clearFilter={clearFilter}
      fieldName={name}
    />
  );
};
